import api from '../../../services/api';
import type { OrderFifoPreviewLine } from './orderProductsApi';

/**
 * POST /api/orders/:id/margin-preview (backend `orderMarginPreviewService`): the
 * live planned margin of the „Ceny sprzedaży” window — the SAVED lines with
 * other sale prices, read-only. The numbers are the server's (the same WP5
 * plan as the stored margin); the page never recomputes them.
 *
 * ADMIN gets every field; the Handlowiec (his own ZK) gets revenue, margin
 * amount and percent and per line only the percent and the cost basis — no
 * `knownCostPLN` and no line costs (`filterOrderMarginPreview`).
 */
export interface OrderMarginPreview {
  revenuePLN: number;
  /** Goods + outgoing transport + other order costs; absent for the Handlowiec. */
  knownCostPLN?: number;
  /** Part of the cost is unknown: no margin, never zero. */
  costPending: boolean;
  marginPLN: number | null;
  marginPercent: number | null;
  lines: OrderFifoPreviewLine[];
}

export interface OrderMarginPreviewPrice {
  /** Saved line id. */
  id: string;
  unitPrice: number;
}

const isFiniteOrNull = (value: unknown) => value === null || (typeof value === 'number' && Number.isFinite(value));

export async function fetchOrderMarginPreview(orderId: string, prices: OrderMarginPreviewPrice[]): Promise<OrderMarginPreview> {
  const { data } = await api.post(`/orders/${orderId}/margin-preview`, { prices });
  if (!data || typeof data.revenuePLN !== 'number' || !Number.isFinite(data.revenuePLN)
    || typeof data.costPending !== 'boolean'
    || !isFiniteOrNull(data.marginPLN) || !isFiniteOrNull(data.marginPercent)
    || (data.knownCostPLN !== undefined && !(typeof data.knownCostPLN === 'number' && Number.isFinite(data.knownCostPLN)))
    || !Array.isArray(data.lines)
    || data.lines.some((line: unknown) => !line || typeof (line as { id?: unknown }).id !== 'string')) {
    throw new Error('Invalid margin preview');
  }
  return data as OrderMarginPreview;
}
